import React, { useState } from 'react';

interface GeoData {
  lat: number;
  lon: number;
  city?: string | null;
  country?: string | null;
}

interface LocationMapProps {
  /** Server-resolved geo (from the session's cached geo lookup, #423). */
  geo?: GeoData | null;
  width?: number;
  height?: number;
}

// Public Mapbox token (build-time env) for the static-map pin image.
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

const LocationMap: React.FC<LocationMapProps> = ({
  geo,
  width = 200,
  height = 120
}) => {
  const [imgError, setImgError] = useState<boolean>(false);

  // Geo is resolved server-side and cached on the session; the proof page no
  // longer calls any third-party geolocation API (was ipapi.co, which 429'd
  // intermittently) and never receives the raw consumer IP.
  if (!geo || typeof geo.lat !== 'number' || typeof geo.lon !== 'number') {
    return (
      <div className="location-map-placeholder" style={{ width, height }}>
        <div className="location-error">Location not available</div>
      </div>
    );
  }

  if (!MAPBOX_ACCESS_TOKEN || imgError) {
    // Graceful fallback: show city/country text even if the map image can't
    // render (no token, or the static-image request failed).
    return (
      <div className="location-map-placeholder" style={{ width, height }}>
        <div className="location-text">
          {geo.city && geo.country ? `${geo.city}, ${geo.country}` : 'Location recorded'}
        </div>
      </div>
    );
  }

  const mapboxUrl = `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/pin-s-circle+ff0000(${geo.lon},${geo.lat})/${geo.lon},${geo.lat},8/${width}x${height}?access_token=${MAPBOX_ACCESS_TOKEN}`;

  return (
    <div className="location-map-container">
      <img
        src={mapboxUrl}
        alt={`Map showing ${geo.city ?? ''}${geo.city && geo.country ? ', ' : ''}${geo.country ?? ''}`.trim() || 'Session location map'}
        className="location-map-image"
        style={{ width, height }}
        onError={() => setImgError(true)}
      />
      {geo.city && geo.country && (
        <div className="location-text">
          {geo.city}, {geo.country}
        </div>
      )}
    </div>
  );
};

export default LocationMap;
